import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';
import { useFormatDate } from '../../hooks/useFormatDate';
import { T } from '../../translation/src';
import { SubscriptionType, SuperadminPlanChangePreviewQuery } from '../../types/graphqlTypes';

type Segment = SuperadminPlanChangePreviewQuery['superadminPlanChangePreview']['timeline'][number];

// One colour per plan, the same in both themes. The rest of the month an
// upgrade lands in (its old allowance plus the extra projects, free) is a
// lighter shade.
const PLAN_COLOURS: Record<SubscriptionType, string> = {
  [SubscriptionType.Flex]: 'bg-gray-400',
  [SubscriptionType.Launch]: 'bg-sky-500',
  [SubscriptionType.Grow]: 'bg-neon-green-500',
  [SubscriptionType.Scale]: 'bg-purple-500',
  [SubscriptionType.Custom]: 'bg-amber-500',
};
const LANDING_COLOURS: Record<SubscriptionType, string> = {
  [SubscriptionType.Flex]: 'bg-gray-300',
  [SubscriptionType.Launch]: 'bg-sky-300',
  [SubscriptionType.Grow]: 'bg-neon-green-300',
  [SubscriptionType.Scale]: 'bg-purple-300',
  [SubscriptionType.Custom]: 'bg-amber-300',
};
const colourOf = (segment: Segment) => (segment.extraCredits > 0 ? LANDING_COLOURS[segment.subscriptionType] : PLAN_COLOURS[segment.subscriptionType]);
// A downgrade's days, whatever the plan: what the customer steps down to stands out from what they have.
const DOWNGRADE_COLOUR = 'bg-orange-500';

const DAY = 24 * 60 * 60 * 1000;
const utcDay = (date: Date | string | number) => Math.floor(new Date(date).getTime() / DAY);
// How far past the term the calendar shows what follows it: to the end of that month, a week at least.
const TAIL_MIN_DAYS = 7;

interface Day {
  // Days since the epoch, in UTC — the key the tooltip looks up.
  key: number;
  date: Date;
  segment: Segment;
  // First day of a period or of a stretch after the change: where something changes.
  starts: boolean;
}

interface Month {
  label: string;
  // Weekday of the first day (Monday = 0) so months align on a weekday grid.
  offset: number;
  days: Day[];
}

// What follows the term when the subscription ends: a stretch of pay-as-you-go days after the last one, so
// the end shows in the calendar itself — to the end of that month, a week at least.
const tailAfter = (timeline: Segment[], downgrade: Downgrade | undefined): Segment[] => {
  const last = timeline[timeline.length - 1];
  if (!last || !downgrade || utcDay(downgrade.from) <= utcDay(last.to)) return [];
  const from = new Date(utcDay(downgrade.from) * DAY);
  const endOfMonth = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 0));
  const to = new Date(Math.max(endOfMonth.getTime(), from.getTime() + (TAIL_MIN_DAYS - 1) * DAY) + DAY - 1);
  return [{ from: from.getTime(), to: to.getTime(), subscriptionType: downgrade.to, monthlyCredits: 0, extraCredits: 0, month: last.month + 1 }];
};

// The term's days, grouped by calendar month.
const useMonths = (timeline: Segment[], downgrade: Downgrade | undefined): Month[] =>
  useMemo(() => {
    const months: Month[] = [];
    let current: Month | undefined;
    for (const segment of [...timeline, ...tailAfter(timeline, downgrade)]) {
      const first = utcDay(segment.from);
      const last = utcDay(segment.to);
      for (let key = first; key <= last; key++) {
        const date = new Date(key * DAY);
        const label = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
        if (!current || current.label !== label) {
          current = { label, offset: (date.getUTCDay() + 6) % 7, days: [] };
          months.push(current);
        }
        current.days.push({ key, date, segment, starts: key === first });
      }
    }
    return months;
  }, [timeline, downgrade]);

// A downgrade: the day it takes effect and the plan it steps down to (FLEX: the subscription ends).
interface Downgrade {
  from: Date;
  to: SubscriptionType;
}

interface PlanCalendarProps {
  timeline: Segment[];
  // The day the change takes effect, outlined.
  changeDay?: Date;
  // A downgrade: its days are coloured as such, from its day on.
  downgrade?: Downgrade;
}

// The term after a change as a calendar: one cell per day, coloured by plan;
// hovering a day says which plan and how many monthly projects apply then.
const PlanCalendar_: React.FC<PlanCalendarProps> = ({ timeline, changeDay, downgrade }) => {
  const months = useMonths(timeline, downgrade);
  const plans = useSubscriptionTypeTranslations();
  const { formatDay } = useFormatDate();
  const [hovered, setHovered] = useState<Day | undefined>(undefined);
  // Where the pointer is, so the tooltip follows it (fixed: the modal body scrolls).
  const [pointer, setPointer] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const monthNames = useMemo(() => new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' }), []);
  const changeKey = changeDay ? utcDay(changeDay) : undefined;
  const downgradeKey = downgrade ? utcDay(downgrade.from) : undefined;
  const isDowngraded = (day: Day) => downgradeKey != null && day.key >= downgradeKey;
  const todayKey = utcDay(new Date());

  // One listener for the whole grid: hundreds of cells, one tooltip.
  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setPointer({ x: event.clientX, y: event.clientY });
      const key = (event.target as HTMLElement).dataset.day;
      if (key == null) {
        setHovered(undefined);
        return;
      }
      setHovered((current) => (current?.key === Number(key) ? current : months.flatMap((month) => month.days).find((candidate) => candidate.key === Number(key))));
    },
    [months],
  );
  const onMouseLeave = useCallback(() => setHovered(undefined), []);

  if (!months.length) return null;
  // The plans the customer has along the way; what they step down to is listed as the downgrade.
  const plansShown = Array.from(new Set(timeline.filter((segment) => downgradeKey == null || utcDay(segment.from) < downgradeKey).map((segment) => segment.subscriptionType)));
  const landing = timeline.find((segment) => segment.extraCredits > 0);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3" onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
        {months.map((month) => (
          <div key={month.label} className="flex flex-col gap-1">
            <span className="text-xs text-gray-500">{monthNames.format(month.days[0].date)}</span>
            {/* Whole-pixel cells and gaps: fractional columns round differently per column, which shows as days
                that are a pixel wider or further apart than their neighbours. */}
            <div className="grid grid-cols-[repeat(7,1.125rem)] justify-start gap-0.5 md:grid-cols-[repeat(7,1.5rem)]">
              {Array.from({ length: month.offset }, (_, i) => (
                <span key={`pad-${i}`} />
              ))}
              {month.days.map((day) => (
                <span
                  key={day.key}
                  data-day={day.key}
                  className={classNames(
                    'h-3 w-full rounded-sm',
                    isDowngraded(day) ? DOWNGRADE_COLOUR : colourOf(day.segment),
                    // Where a period starts: a bar inside the cell. The cell keeps its outline, so the day stays
                    // in line with the ones around it (a border in the background colour made it look narrower).
                    day.starts && 'shadow-[inset_3px_0_0_rgb(0_0_0/0.45)]',
                    day.key === changeKey && 'ring-2 ring-black dark:ring-white',
                    day.key === todayKey && 'ring-1 ring-gray-500',
                    hovered?.key === day.key ? 'opacity-100' : hovered && hovered.segment !== day.segment && 'opacity-60',
                  )}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <div className="flex flex-wrap gap-3">
          {plansShown.map((plan) => (
            <span key={plan} className="flex items-center gap-1 text-gray-500">
              <span className={classNames('inline-block h-2.5 w-2.5 rounded-sm', PLAN_COLOURS[plan])} />
              {plans[plan]}
            </span>
          ))}
          {downgrade && (
            <span className="flex items-center gap-1 text-gray-500">
              <span className={classNames('inline-block h-2.5 w-2.5 rounded-sm', DOWNGRADE_COLOUR)} />
              {plans[downgrade.to]} · <T _str="downgrade" />
            </span>
          )}
          {landing && (
            <span className="flex items-center gap-1 text-gray-500">
              <span className={classNames('inline-block h-2.5 w-2.5 rounded-sm', LANDING_COLOURS[landing.subscriptionType])} />
              <T _str="upgrade" />
            </span>
          )}
        </div>
      </div>
      {hovered && (
        <div
          className="pointer-events-none fixed z-50 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-black shadow-lg dark:border-gray-600 dark:bg-eerie-black dark:text-white"
          style={{ left: pointer.x + 12, top: pointer.y + 12 }}
        >
          <span className="font-medium">{formatDay(hovered.date)}</span> · {plans[hovered.segment.subscriptionType]}
          {hovered.segment.subscriptionType === SubscriptionType.Flex ? (
            <>
              {' '}
              · <T _str="pay as you go" />
            </>
          ) : (
            <>
              {' '}
              · <T _str="{credits} project(s)" args={{ credits: hovered.segment.monthlyCredits }} />
            </>
          )}
          {hovered.segment.extraCredits > 0 && (
            <>
              {' '}
              (<T _str="{base} + {extra} extra" args={{ base: hovered.segment.monthlyCredits - hovered.segment.extraCredits, extra: hovered.segment.extraCredits }} />)
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const PlanCalendar = memo(PlanCalendar_);
